export interface Persona {
  id: string;
  name: string;
  persona_name: string | null;
  age: number | null;
  gender: string | null;
  job_title: string | null;
  company_type: string | null;
  bio: string | null;
  goals_motivations: string | null;
  frustrations: string | null;
  preferred_tools: string | null;
  sample_quote: string | null;
  basic_demographics: string | null;
  professional_background: string | null;
  user_needs_goals: string | null;
  information_sources: string | null;
  influencing_factors: string | null;
  site_access_context: string | null;
  regular_devices: string | null;
  software_applications: string | null;
  primary_web_device: string | null;
  daily_browsing_time: string | null;
  work_experience: string | null;
  communication_channels: string | null;
  technology_comfort: string | null;
  behavioral_patterns: string | null;
}

export interface PersonaBrief {
  id: string;
  name: string;
  form_data: any;
  generated_brief: string | null;
}

export type PersonaFormat = 'brief' | 'detailed' | 'full';

export const formatPersona = (persona: Persona, format: PersonaFormat = 'brief'): string => {
  const displayName = persona.persona_name || persona.name;
  
  if (format === 'brief') {
    const basicInfo = [];
    if (persona.age) basicInfo.push(`${persona.age}+ ${persona.gender}`);
    if (persona.job_title) basicInfo.push(persona.job_title);
    if (persona.company_type) basicInfo.push(`at ${persona.company_type}`);
    
    const summary = `${displayName}${basicInfo.length ? ` (${basicInfo.join(', ')})` : ''}`;
    
    if (persona.goals_motivations) {
      return `${summary} who ${persona.goals_motivations.toLowerCase()}`;
    }
    
    return summary;
  }
  
  if (format === 'detailed') {
    const parts = [`${displayName}`];
    
    // Demographics
    const demo = [];
    if (persona.age) demo.push(`${persona.age} years old`);
    if (persona.gender) demo.push(persona.gender);
    if (persona.job_title) demo.push(persona.job_title);
    if (persona.company_type) demo.push(`at ${persona.company_type}`);
    if (demo.length) parts.push(`(${demo.join(', ')})`);
    
    // Goals and motivations
    if (persona.goals_motivations) {
      parts.push(`who aims to ${persona.goals_motivations.toLowerCase()}`);
    }
    
    // Key frustrations
    if (persona.frustrations) {
      parts.push(`Frustrated by ${persona.frustrations.toLowerCase()}`);
    }
    
    // Preferred tools
    if (persona.preferred_tools) {
      parts.push(`Uses ${persona.preferred_tools}`);
    }
    
    return parts.join('. ').replace(/\.\./g, '.');
  }
  
  // Full format
  const sections = [];
  
  sections.push(`**${displayName}**`);
  
  if (persona.bio) sections.push(persona.bio);
  
  const details = [];
  if (persona.age && persona.gender) details.push(`Age: ${persona.age}, Gender: ${persona.gender}`);
  if (persona.job_title) details.push(`Role: ${persona.job_title}`);
  if (persona.company_type) details.push(`Company: ${persona.company_type}`);
  if (details.length) sections.push(details.join(' | '));
  
  if (persona.goals_motivations) sections.push(`Goals: ${persona.goals_motivations}`);
  if (persona.frustrations) sections.push(`Frustrations: ${persona.frustrations}`);
  if (persona.preferred_tools) sections.push(`Tools: ${persona.preferred_tools}`);
  if (persona.technology_comfort) sections.push(`Tech Comfort: ${persona.technology_comfort}`);
  
  return sections.join('\n');
};

export const formatPersonaBrief = (brief: PersonaBrief, format: PersonaFormat = 'brief'): string => {
  if (format === 'brief') {
    return brief.generated_brief || brief.name;
  }
  
  if (format === 'detailed') {
    const parts = [brief.name];
    if (brief.generated_brief) {
      parts.push(brief.generated_brief);
    }
    if (brief.form_data) {
      const formInfo = [];
      if (brief.form_data.industry) formInfo.push(`Industry: ${brief.form_data.industry}`);
      if (brief.form_data.product) formInfo.push(`Product: ${brief.form_data.product}`);
      if (formInfo.length) parts.push(formInfo.join(', '));
    }
    return parts.join('. ');
  }
  
  // Full format
  const sections = [];
  sections.push(`**${brief.name}**`);
  
  if (brief.generated_brief) {
    sections.push(brief.generated_brief);
  }
  
  if (brief.form_data) {
    const formData = brief.form_data;
    if (formData.industry) sections.push(`Industry: ${formData.industry}`);
    if (formData.product) sections.push(`Product: ${formData.product}`);
    if (formData.age) sections.push(`Target Age: ${formData.age}`);
    if (formData.jobRole) sections.push(`Job Role: ${formData.jobRole}`);
    if (formData.goals) sections.push(`Goals: ${formData.goals}`);
    if (formData.geography) sections.push(`Geography: ${formData.geography}`);
    if (formData.painPoints) sections.push(`Pain Points: ${formData.painPoints}`);
  }
  
  return sections.join('\n');
};